/**
 * Game Comparison Page Component
 * 
 * Main page for comparing pull tab games. Provides interface for
 * selecting games, configuring comparison parameters, and viewing
 * detailed comparison results.
 * 
 * Features:
 * - Two-step process: selection then comparison
 * - Persistent state across page reloads
 * - Responsive design for all screen sizes
 * - Integration with authentication
 * 
 * @module TabPicker.ComparePage
 */

import React, { useEffect, useState } from 'react';
import { useComparisonStore } from '../stores/comparisonStore';
import { useAuth } from '../hooks/useAuth';
import { useGetApiGames } from '../api/generated/games/games';
import { GameSelector, DealStatusControls } from '../components/GameSelector';
import { GameComparison } from '../components/GameComparison';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Toast } from '../components/ui/Toast';


/**
 * Step indicator component
 */
const StepIndicator: React.FC<{
  currentStep: 'select' | 'dealStatus' | 'compare';
  onStepChange: (step: 'select' | 'dealStatus' | 'compare') => void;
  canProceedToStatus: boolean;
  canProceedToCompare: boolean;
}> = ({ currentStep, onStepChange, canProceedToStatus, canProceedToCompare }) => {
  const getStepStatus = (step: 'select' | 'dealStatus' | 'compare') => {
    if (step === 'select') {
      return canProceedToStatus ? 'completed' : currentStep === 'select' ? 'active' : 'inactive';
    } else if (step === 'dealStatus') {
      if (currentStep === 'dealStatus') return 'active';
      if (currentStep === 'compare') return 'completed';
      return canProceedToStatus ? 'available' : 'inactive';
    } else { // compare
      if (currentStep === 'compare') return 'active';
      return canProceedToCompare ? 'available' : 'inactive';
    }
  };

  const getStepClasses = (status: string) => {
    switch (status) {
      case 'active':
        return 'border-blue-600 bg-blue-600 text-white';
      case 'completed':
        return 'border-green-500 bg-green-500 text-white';
      case 'available':
        return 'border-gray-400 bg-white text-gray-600';
      default:
        return 'border-gray-300 bg-white text-gray-400';
    }
  };

  return (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        {/* Step 1: Select Games */}
        <div
          className={`flex items-center cursor-pointer ${
            currentStep === 'select' ? 'text-blue-600' : canProceedToStatus ? 'text-gray-600' : 'text-gray-400'
          }`}
          onClick={() => onStepChange('select')}
        >
          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 mr-3 ${getStepClasses(getStepStatus('select'))}`}>
            {getStepStatus('select') === 'completed' ? '✓' : '1'}
          </div>
          <span className="font-medium">Select Games</span>
        </div>

        {/* Connector 1 */}
        <div className={`w-12 h-px ${canProceedToStatus ? 'bg-green-500' : 'bg-gray-300'}`} />

        {/* Step 2: Deal Status */}
        <div
          className={`flex items-center cursor-pointer ${
            currentStep === 'dealStatus' ? 'text-blue-600' : canProceedToStatus ? 'text-gray-600' : 'text-gray-400'
          }`}
          onClick={() => canProceedToStatus && onStepChange('dealStatus')}
        >
          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 mr-3 ${getStepClasses(getStepStatus('dealStatus'))}`}>
            {getStepStatus('dealStatus') === 'completed' ? '✓' : '2'}
          </div>
          <span className="font-medium">Set Deal Status</span>
        </div>

        {/* Connector 2 */}
        <div className={`w-12 h-px ${canProceedToCompare ? 'bg-green-500' : 'bg-gray-300'}`} />

        {/* Step 3: Compare */}
        <div
          className={`flex items-center cursor-pointer ${
            currentStep === 'compare' ? 'text-blue-600' : canProceedToCompare ? 'text-gray-600' : 'text-gray-400'
          }`}
          onClick={() => canProceedToCompare && onStepChange('compare')}
        >
          <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 mr-3 ${getStepClasses(getStepStatus('compare'))}`}>
            3
          </div>
          <span className="font-medium">View Results</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Main Compare Page Component
 */
export default function ComparePage() {
  const { user } = useAuth();
  const {
    availableGames,
    selectedGameIds,
    results,
    canCompare,
    toast,
    setAvailableGames,
    setToast,
    openComparison,
  } = useComparisonStore();

  const [currentStep, setCurrentStep] = useState<'select' | 'dealStatus' | 'compare'>('select');
  
  // Use React Query to fetch user's games
  const { 
    data: gamesData, 
    isLoading: isLoadingGames, 
    error: gamesError 
  } = useGetApiGames(
    undefined, // no query params needed
    { 
      query: { 
        enabled: !!user, // only run query if user is authenticated
        staleTime: 0, // Disable cache for debugging - was 5 * 60 * 1000 
        retry: 2
      } 
    }
  );

  // Convert API response to comparison store format
  useEffect(() => {
    if (gamesData?.games) {
      const gameRefs = gamesData.games.map(game => ({
        id: game.id,
        name: game.name,
        selected: false,
        ticketPrice: game.ticketPrice,
        totalTickets: game.totalTickets,
        totalPayout: game.totalPayout,
        createdAt: game.createdAt,
      }));
      
      setAvailableGames(gameRefs);
    }
  }, [gamesData, setAvailableGames]);

  // Handle API errors
  useEffect(() => {
    if (gamesError) {
      console.error('Failed to load games:', gamesError);
      setToast('Failed to load your games. Please try refreshing the page.');
    }
  }, [gamesError, setToast]);

  // Don't auto-advance anymore since we have a three-step flow
  
  const handleStepChange = (step: 'select' | 'dealStatus' | 'compare') => {
    if (step === 'dealStatus' && !canCompare()) {
      setToast('Please select at least 2 games to continue');
      return;
    }
    if (step === 'compare' && !canCompare()) {
      setToast('Please select games and set deal status first');
      return;
    }
    setCurrentStep(step);
  };

  const handleProceedToDealStatus = () => {
    if (!canCompare()) {
      setToast('Please select at least 2 games to continue');
      return;
    }
    setCurrentStep('dealStatus');
  };

  const handleProceedToComparison = () => {
    setCurrentStep('compare');
    openComparison();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 max-w-md mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h1>
            <p className="text-gray-600 mb-6">
              Please sign in to compare your games.
            </p>
            <Button onClick={() => window.location.href = '/'}>
              Go to Sign In
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-2 tablet:px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Compare Games
              </h1>
              <p className="text-gray-600 mt-1">
                Analyze and compare your pull tab games side-by-side
              </p>
            </div>
            <Button
              onClick={() => window.history.back()}
              variant="secondary"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-2 tablet:px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Step Indicator */}
          <StepIndicator
            currentStep={currentStep}
            onStepChange={handleStepChange}
            canProceedToStatus={canCompare()}
            canProceedToCompare={canCompare()}
          />

          {/* Loading State */}
          {isLoadingGames && (
            <Card className="p-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading your games...</p>
              </div>
            </Card>
          )}

          {/* Content */}
          {!isLoadingGames && (
            <>
              {currentStep === 'select' && (
                <Card className="p-6">
                  <div className="mb-6">
                    <h2 className="text-2xl tablet:text-xl font-semibold text-gray-900 mb-2">
                      Select Games to Compare
                    </h2>
                    <p className="text-gray-600">
                      Choose 2-10 games to analyze side-by-side. Compare odds, expected values, and rankings.
                    </p>
                  </div>

                  <GameSelector games={availableGames} />

                  {canCompare() && (
                    <div className="mt-8 flex justify-center">
                      <Button
                        onClick={handleProceedToDealStatus}
                        size="lg"
                        className="px-8"
                      >
                        Continue with {selectedGameIds.length} Games
                      </Button>
                    </div>
                  )}
                </Card>
              )}

              {currentStep === 'dealStatus' && (
                <Card className="p-6">
                  <div className="mb-6">
                    <h2 className="text-2xl tablet:text-xl font-semibold text-gray-900 mb-2">
                      Set Deal Status
                    </h2>
                    <p className="text-gray-600">
                      Adjust jar fill percentages to reflect real-world game conditions and see how it affects the odds.
                    </p>
                  </div>

                  {/* Show selected games and deal status controls */}
                  <div className="space-y-6">
                    {/* Selected games preview */}
                    <Card className="py-3 px-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-700">
                          Selected Games ({selectedGameIds.length})
                        </h3>
                        <Button
                          onClick={() => setCurrentStep('select')}
                          variant="secondary"
                          size="sm"
                        >
                          Change Selection
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {availableGames
                          .filter(game => selectedGameIds.includes(game.id))
                          .map(game => (
                            <span
                              key={game.id}
                              className="px-3 py-1 bg-white rounded-full text-base tablet:text-sm text-gray-700 border"
                            >
                              {game.name}
                            </span>
                          ))}
                      </div>
                    </Card>

                    {/* Deal Status Controls */}
                    <DealStatusControls 
                      selectedGames={availableGames.filter(g => selectedGameIds.includes(g.id))} 
                    />
                  </div>

                  <div className="mt-8 flex justify-center gap-4">
                    <Button
                      onClick={() => setCurrentStep('select')}
                      variant="secondary"
                      size="lg"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleProceedToComparison}
                      size="lg"
                      className="px-8"
                    >
                      View Comparison Results
                    </Button>
                  </div>
                </Card>
              )}

              {currentStep === 'compare' && (
                <Card className="p-6">
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl tablet:text-xl font-semibold text-gray-900">
                        Comparison Results
                      </h2>
                      <p className="text-gray-600 mt-1">
                        Detailed analysis of {selectedGameIds.length} selected games
                      </p>
                    </div>
                    <Button
                      onClick={() => setCurrentStep('dealStatus')}
                      variant="secondary"
                    >
                      Adjust Deal Status
                    </Button>
                  </div>

                  <GameComparison />
                </Card>
              )}
            </>
          )}
        </div>
      </main>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast}
          type="info"
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}